import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Alert, Button, Checkbox, Modal, Typography, notification } from "antd";
// COPY TO CLIPBOARD
import { CopyToClipboard } from "react-copy-to-clipboard";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faKey } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
// INTERFACES
import { RootState } from "../../app/store";

const { Paragraph, Text } = Typography;

interface OfflinePasswordHandoffProps {
  /** The one-time password, exactly as the server returned it. */
  password: string;
  /** Who it belongs to — an operator provisioning several accounts needs this. */
  userLabel: string;
  open: boolean;
  onClose: () => void;
}

/**
 * The hand-off step of offline user provisioning.
 *
 * An edge server creates a user with a password IT generated, and stores only
 * an argon2id hash of it. That means this dialog is the single moment the
 * plaintext exists anywhere a person can read it: close it without copying the
 * password down and the only remedy is to reset the account and start again.
 * Everything here follows from that.
 *
 *  - The password is rendered as selectable text AND offered on the clipboard,
 *    because an engineer may be reading it onto a phone rather than pasting.
 *  - Dismissal requires ticking an acknowledgement. A dialog this consequential
 *    should not close because someone pressed Escape on the way past, so the
 *    mask and the keyboard shortcut are both disabled.
 *  - It says what the recipient must do next. The account cannot be used for
 *    anything until they replace this password, and an operator who does not
 *    know that reads the first refusal as a broken account.
 *
 * The password is deliberately never written anywhere that outlives the dialog
 * — no logging, no storage, no Redux. It arrives as a prop and dies with the
 * component's state.
 */
function OfflinePasswordHandoff({
  password,
  userLabel,
  open,
  onClose,
}: OfflinePasswordHandoffProps) {
  const { theme } = useSelector((state: RootState) => state.home);
  const [acknowledged, setAcknowledged] = useState(false);

  const handleClose = () => {
    setAcknowledged(false);
    onClose();
  };

  const onCopy = () => {
    notification.open({
      message: <span className="generalStyles__success">Password copied</span>,
      description: "Paste it somewhere safe before closing this dialog.",
      placement: "top",
      className: `monitoring__notification__${theme}`,
    });
  };

  return (
    <Modal
      title={
        <span>
          <FontAwesomeIcon icon={faKey} className="generalStyles__info generalStyles__mrFix" />
          Offline password for {userLabel}
        </span>
      }
      open={open}
      // Both closes are removed on purpose: this is the only time the password
      // is readable, so it must not vanish on a stray Escape or backdrop click.
      closable={false}
      keyboard={false}
      maskClosable={false}
      onCancel={handleClose}
      footer={[
        <Button key="done" type="primary" disabled={!acknowledged} onClick={handleClose}>
          Done
        </Button>,
      ]}
    >
      <Alert
        type="warning"
        showIcon
        message="Shown once"
        description="The server keeps only a hashed copy. If you close this without saving the password, it cannot be recovered — the account has to be reset."
        style={{ marginBottom: 16 }}
      />

      <Paragraph>Give this password to the person who will sign in on this edge server.</Paragraph>

      <div
        className={`drawer__box__${theme}`}
        style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}
      >
        <Text code copyable={false} style={{ fontSize: 16, flex: 1, wordBreak: "break-all" }}>
          {password}
        </Text>
        <CopyToClipboard text={password} onCopy={onCopy}>
          <Button icon={<FontAwesomeIcon icon={faCopy} />}>Copy</Button>
        </CopyToClipboard>
      </div>

      <Paragraph style={{ marginTop: 16 }}>
        They will be asked to replace it the first time they sign in. Until they do, the account
        cannot be used for anything else — that is expected, not a fault.
      </Paragraph>

      <Checkbox
        checked={acknowledged}
        onChange={(event) => setAcknowledged(event.target.checked)}
        data-testid="offline-password-acknowledged"
      >
        I have saved this password
      </Checkbox>
    </Modal>
  );
}

export default OfflinePasswordHandoff;
